import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';

import { PRISM_DIALOG_DATA, PrismDialogRef } from '@shure/prism-angular-components';

import { getFormattedUrl } from '../../footer/footer-utils';
import { mobilePhoneValidator, workPhoneValidator } from '../../utils';
import { FormTypes } from '../dialog-box-ignite/dialog-box.model';

import {
	AccountInfoDto,
	industryOptions,
	interestsOptions,
	ProgProfileFormDataDetails,
	relationshipOptions
} from './progressive-profile-dialog.model';

@Component({
	selector: 'sh-progressive-profile-dialog',
	templateUrl: './progressive-profile-dialog.component.html',
	styleUrls: ['./progressive-profile-dialog.component.scss']
})
export class ShProgressiveProfileDialogComponent implements OnInit {
	public formGroup: FormGroup = new FormGroup({});
	public formData: ProgProfileFormDataDetails[] = [];
	public formType = FormTypes;
	public interestsOptions = interestsOptions;
	public relationshipOptions = relationshipOptions;
	public termsUrl!: string;
	private destroy$: Subject<void> = new Subject<void>();
	public isCloseOnEsc = true;
	constructor(
		private formBuilder: FormBuilder,
		private translocoService: TranslocoService,
		@Inject(PRISM_DIALOG_DATA) public progFormData: ProgProfileFormDataDetails[],
		public dialogRef: PrismDialogRef<ShProgressiveProfileDialogComponent>
	) {}

	// HostListener to listen for the "Enter" keypress and trigger form validation
	@HostListener('window:keydown.enter', ['$event'])
	public handleEnterKey(event: KeyboardEvent): void {
		event.preventDefault();
		this.formGroup.markAllAsTouched();
		if (this.formGroup.valid) {
			this.onSubmit();
		}
	}
	public ngOnInit(): void {
		this.termsUrl = getFormattedUrl('TERMS_CONDITIONS');
		this.formGroup = this.formBuilder.group({});
		this.formData = this.progFormData;

		//  Check if 'tc' field exists in progFormData
		this.isCloseOnEsc = this.closeOnEsc();
		this.formData?.forEach((formControlDetail) => {
			if (formControlDetail.type === 'checkbox') {
				const checkboxFormGroup = this.formBuilder.group({});
				if (formControlDetail.name === 'interests') {
					this.interestsOptions.forEach((option) => {
						checkboxFormGroup.addControl(option.name, new FormControl(false));
					});
				} else if (formControlDetail.name === 'relationToShure') {
					this.relationshipOptions.forEach((option) => {
						checkboxFormGroup.addControl(option.name, new FormControl(false));
					});
				} else if (formControlDetail.name === 'tc') {
					checkboxFormGroup.addControl('isTcAccepted', new FormControl(false, formControlDetail.validators));
				}
				this.formGroup.addControl(formControlDetail.name, checkboxFormGroup);
			} else if (formControlDetail.type === 'text') {
				const validators = [];
				if (formControlDetail.name === 'mobilePhone') {
					validators.push(mobilePhoneValidator); // Add your custom validator
				}
				if (formControlDetail.name === 'workPhone') {
					validators.push(workPhoneValidator);
				}
				this.formGroup.addControl(formControlDetail.name, new FormControl('', ...validators));
			} else {
				this.formGroup.addControl(formControlDetail.name, new FormControl(''));
			}
			if (formControlDetail.disabled) {
				this.formGroup.controls[formControlDetail.name].disable();
			}
		});
	}

	public onBlur(controlName: string): void {
		const control = this.formGroup?.get(controlName);
		if (control?.valid && controlName === 'mobilePhone') {
			const formatted = this.formatPhoneNumber(control.value || '');
			control.setValue(formatted, { emitEvent: false });
		}
		if (controlName === 'workPhone') {
			const formatted = this.formatWorkPhone(control?.value || '');
			control?.setValue(formatted, { emitEvent: false });
		}
	}

	public formatPhoneNumber(input: string): string {
		// Remove all non-numeric characters except '+'
		input = input.replace(/[^\d+]/g, '');

		if (!input) {
			return '';
		}

		// Ensure the phone number starts with '+'
		if (!input.startsWith('+')) {
			input = '+' + input;
		}

		// Format to +1-312-123-4567 pattern
		return input.replace(/^(\+\d{1,4})(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3-$4');
	}

	public formatWorkPhone(input: string): string {
		// Strip out any non-digit characters
		const raw = input.replace(/\D/g, '');

		// Format only if 10 digits & Format to 123-456-7896
		return raw.length === 10 ? `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}` : input;
	}

	/* This method is called when the user selects an industry
	 * and must set the respective industry details data for selection
	 * and enable industry details form control
	 */
	public segmentSelected(event: string): void {
		const industryDetailSelected = industryOptions.find((industryItem) => industryItem.value === event)
			?.industryDetail;

		const index = this.formData?.findIndex((formRow) => formRow.name === 'segmentDetails');
		if (index !== -1 && this.formData) {
			this.formData[index].data = industryDetailSelected;
		}

		const segmentDetailsControl = this.formGroup.get('segmentDetails');
		if (segmentDetailsControl) {
			segmentDetailsControl.reset();
			segmentDetailsControl.enable();
		}
	}

	// This method is called when the user submits the data
	public onSubmit(): void {
		if (this.formGroup.valid) {
			const formGroupValue = this.formGroup.value;
			// Remove hyphens from mobilePhone
			if (formGroupValue.mobilePhone) {
				formGroupValue.mobilePhone = formGroupValue.mobilePhone.replace(/-/g, '');
			}
			const updateAccountData = this.formatData(formGroupValue);
			this.dialogRef.close({ data: updateAccountData });
		}
	}

	/* it must format the form group submitted data into the type of AccoutInfoDto
	 * and return the formatted data.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public formatData(formGroupValue: any): AccountInfoDto {
		Object.keys(formGroupValue).forEach((key) => {
			if (key === 'interests' || key === 'relationToShure') {
				const checkboxSelectedOptions = Object.entries(formGroupValue[key]).filter(
					([, value]) => value === true
				);
				const checkboxSelectedOptionsKeys = checkboxSelectedOptions.map(([key]) => key);

				if (key === 'interests') {
					formGroupValue[key] = this.interestsOptions
						.filter((interest) => checkboxSelectedOptionsKeys.includes(interest.name))
						.map((filteredInterest) => filteredInterest.value);
				} else {
					formGroupValue[key] = this.relationshipOptions
						.filter((relationship) => checkboxSelectedOptionsKeys.includes(relationship.name))
						.map((filteredRelations) => filteredRelations.value);
				}
			}
			if (key === 'departments') {
				formGroupValue.department = formGroupValue.departments;
				delete formGroupValue.departments;
			}
			if (key === 'segments') {
				formGroupValue.segmentDetails = formGroupValue.segments;
				delete formGroupValue.segments;
			}
			if (formGroupValue[key] === '' || formGroupValue[key] === undefined || formGroupValue[key].length <= 0) {
				delete formGroupValue[key];
			}
		});
		return formGroupValue;
	}

	public getPhoneErrorMessage(type: string): string {
		const control = this.formGroup.get(type);
		if (!control || !control.errors) return '';
		if (type === 'mobilePhone') {
			if (control.hasError('missingCountryCode')) {
				return this.translocoService.translate('cloud.shared.error-labels.country-code-missing');
			}
			if (control.hasError('invalidCharacters')) {
				return this.translocoService.translate(
					'cloud.shared.error-labels.mobile-number-contains-invalid-characters'
				);
			}
			if (control.hasError('invalidLength')) {
				return this.translocoService.translate('cloud.shared.error-labels.mobile-number-length');
			}
			if (control.hasError('invalidMobileNumber')) {
				return this.translocoService.translate('cloud.shared.error-labels.invalid-mobile-number');
			}
		}

		if (type === 'workPhone') {
			if (control.hasError('invalidWorkPhoneFormat') || control.hasError('invalidCharacters')) {
				return this.translocoService.translate(
					'cloud.shared.error-labels.work-phone-contains-invalid-characters'
				);
			}
		}

		return '';
	}

	/**
	 * Determines whether the dialog should close on ESC.
	 * Returns true if 'tc' field is NOT present in progFormData.
	 */
	private closeOnEsc(): boolean {
		const hasTcField = this.progFormData?.some((field) => field.name === 'tc');
		return !hasTcField;
	}
}
